/**
 * Reads documentation/portal-release-notes.md - the record itself, bundled
 * at build time so the page can never drift from the file - into the few
 * shapes that file is allowed to contain (CLAUDE.md section 9): a `##`
 * per version, `###` per heading, bullets, an intro paragraph or two, and
 * `[#nn]: url` link definitions at the bottom. Nothing before the first
 * `##` is rendered; those are the team's rules, not a user's changelog.
 */
export type Inline =
  | { kind: "text"; text: string }
  | { kind: "code"; text: string }
  | { kind: "strong"; text: string }
  | { kind: "em"; text: string }
  | { kind: "link"; text: string; href: string };

export interface NoteSection {
  heading: string;
  bullets: Inline[][];
}

export type NoteLine = "portal" | "template" | "extension";

export interface NoteVersion {
  /** `unreleased`, or `v0.2.0` - the same string `vtag()` puts in the footer, so the footer can link straight here. Template versions are prefixed `template-`, extension versions `extension-`. */
  id: string;
  /** Which deliverable's notes this is (CLAUDE.md section 9 has three lines). */
  line: NoteLine;
  version: string;
  date?: string;
  intro: Inline[][];
  sections: NoteSection[];
}

export interface ReleaseNotes {
  versions: NoteVersion[];
}

export function parseReleaseNotes(markdown: string, noteLine: NoteLine = "portal"): ReleaseNotes {
  const lines = markdown.split(/\r?\n/);
  const refs = new Map<string, string>();
  for (const line of lines) {
    const m = /^\[([^\]]+)\]:\s*(\S+)\s*$/.exec(line);
    if (m) refs.set(m[1]!, m[2]!);
  }

  const versions: NoteVersion[] = [];
  let version: NoteVersion | undefined;
  let section: NoteSection | undefined;
  let paragraph: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length && version) version.intro.push(inline(paragraph.join(" "), refs));
    paragraph = [];
  };

  for (const raw of lines) {
    const line = raw.replace(/\s+$/, "");
    if (line.startsWith("## ")) {
      flushParagraph();
      const m = /^## (\S+)(?:\s+[—-]+\s+(\d{4}-\d{2}-\d{2}))?$/.exec(line);
      const name = m?.[1] ?? line.slice(3);
      const id = name === "Unreleased" ? "unreleased" : `v${name}`;
      version = { id: noteLine === "portal" ? id : `${noteLine}-${id}`, line: noteLine, version: name, intro: [], sections: [] };
      if (m?.[2]) version.date = m[2];
      versions.push(version);
      section = undefined;
      continue;
    }
    if (!version) continue;
    if (line.startsWith("### ")) {
      flushParagraph();
      section = { heading: line.slice(4), bullets: [] };
      version.sections.push(section);
      continue;
    }
    if (/^\[[^\]]+\]:/.test(line)) continue;
    if (line.startsWith("- ")) {
      flushParagraph();
      section ??= { heading: "", bullets: [] };
      if (!version.sections.includes(section)) version.sections.push(section);
      section.bullets.push([{ kind: "text", text: line.slice(2) }]);
      continue;
    }
    if (/^ {2,}\S/.test(line) && section?.bullets.length) {
      const last = section.bullets[section.bullets.length - 1]!;
      last[0] = { kind: "text", text: `${(last[0] as { text: string }).text} ${line.trim()}` };
      continue;
    }
    if (line === "") {
      flushParagraph();
      continue;
    }
    paragraph.push(line.trim());
  }
  flushParagraph();

  for (const v of versions) for (const s of v.sections) s.bullets = s.bullets.map((b) => inline((b[0] as { text: string }).text, refs));
  return { versions };
}

export const plainText = (parts: Inline[]): string => parts.map((p) => p.text).join("");

const INLINE = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\[([^\]]+)\]\(([^)\s]+)\))|(\[(#\d+)\])|((?<![\w`])_[^_]+_(?![\w`]))|((?<![\w`*])\*[^*]+\*(?![\w`*]))/g;

function inline(text: string, refs: Map<string, string>): Inline[] {
  const out: Inline[] = [];
  let last = 0;
  for (const m of text.matchAll(INLINE)) {
    if (m.index! > last) out.push({ kind: "text", text: text.slice(last, m.index) });
    if (m[1]) out.push({ kind: "code", text: m[1].slice(1, -1) });
    else if (m[2]) out.push({ kind: "strong", text: m[2].slice(2, -2) });
    else if (m[3]) out.push({ kind: "link", text: m[4]!, href: m[5]! });
    else if (m[6]) {
      const href = refs.get(m[7]!);
      out.push(href ? { kind: "link", text: m[7]!, href } : { kind: "text", text: m[7]! });
    } else if (m[8]) out.push({ kind: "em", text: m[8].slice(1, -1) });
    else if (m[9]) out.push({ kind: "em", text: m[9].slice(1, -1) });
    last = m.index! + m[0].length;
  }
  if (last < text.length) out.push({ kind: "text", text: text.slice(last) });
  return out;
}
